/**
 * PostgREST returns at most 1000 rows for a query that doesn't ask for a range,
 * and it does so silently — no error, no flag, just a short array. Any view that
 * fetches a whole table and then filters/aggregates in JavaScript will quietly
 * start dropping the newest rows once the table crosses that cap.
 *
 * fetchAllRows pages through the full result set instead.
 *
 * Pass a builder function, not a query — each page needs a fresh builder:
 *
 *   const rows = await fetchAllRows(() =>
 *     supabase.from('dispatches').select('id, status').neq('status', 'removed')
 *   );
 *
 * A stable sort is required for correct paging, otherwise rows can repeat or
 * vanish between pages. `orderColumn` is applied last, so it acts as a
 * tiebreaker if the builder already sorts by something else.
 */
export async function fetchAllRows<T = any>(
  build: () => any,
  options: { pageSize?: number; orderColumn?: string } = {}
): Promise<T[]> {
  const { pageSize = 1000, orderColumn = 'id' } = options;
  const rows: T[] = [];

  for (let page = 0; ; page++) {
    const { data, error } = await build()
      .order(orderColumn, { ascending: true })
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (error) throw error;

    rows.push(...((data || []) as T[]));
    if (!data || data.length < pageSize) return rows;
  }
}
